import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import { searchNews } from '../../ScenarioBuilder/CorpusNews/api';
import TagFilter, {
  CATEGORY_LABELS,
} from '../../ScenarioBuilder/CorpusNews/TagFilter';
import { TagOption } from '../../ScenarioBuilder/CorpusNews/types';
import { NewsCuration } from '../../MarketExplorer/types';
import { errorMessage } from '../../MarketExplorer/api';
import { BlueprintNewsFeed, changeBlueprintNews } from './presentationApi';

type Props = {
  open: boolean;
  blueprintId: number;
  variantId: number;
  feed: BlueprintNewsFeed;
  onClose: () => void;
  onAdded: (feed: BlueprintNewsFeed) => void;
};

const day = (value: string) => value.slice(0, 10);

/** Find AI Curated stories inside the exercise's dates and add the chosen ones to this version's news. */
const AddNewsModal: React.FC<Props> = ({
  open,
  blueprintId,
  variantId,
  feed,
  onClose,
  onAdded,
}) => {
  const [query, setQuery] = useState('');
  const [start, setStart] = useState(day(feed.start_date));
  const [end, setEnd] = useState(day(feed.end_date));
  const [tags, setTags] = useState<TagOption[]>([]);
  const [rows, setRows] = useState<NewsCuration[]>([]);
  const [categories, setCategories] = useState<Record<string, string>>({});
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [next, setNext] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const request = useRef<AbortController | null>(null);
  const inBlueprint = new Set(feed.items.map(item => item.id));

  const load = async (more = false, chosen: TagOption[] = tags) => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    setError('');
    try {
      const page = await searchNews(
        {
          q: query.trim(),
          after_id: more ? next : 0,
          limit: 50,
          source: 'ai_curated',
          ...(start ? { date_start: start } : {}),
          ...(end ? { date_end: end } : {}),
          ...(chosen.length
            ? { tag_ids: chosen.map(tag => tag.id).join(',') }
            : {}),
        },
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setRows(current => (more ? [...current, ...page.results] : page.results));
      setCategories(current => ({ ...current, ...(page.categories || {}) }));
      setLabels(current => ({ ...current, ...(page.tag_labels || {}) }));
      setNext(page.next_after_id || 0);
      setHasMore(page.has_more);
    } catch (reason) {
      if (!controller.signal.aborted) setError(errorMessage(reason));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    if (open) void load();
    return () => request.current?.abort();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    setAdding(true);
    setError('');
    try {
      const updated = await changeBlueprintNews(blueprintId, variantId, {
        add: selected,
      });
      setSelected([]);
      onAdded(updated);
      onClose();
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setAdding(false);
    }
  };

  const columns: TableColumnsType<NewsCuration> = [
    {
      title: 'Date (UTC)',
      key: 'date',
      width: 150,
      render: (_value, item) =>
        item.timestamp
          ? new Date(item.timestamp)
              .toISOString()
              .slice(0, 16)
              .replace('T', ' ')
          : '',
    },
    {
      title: 'AI Curated headline',
      key: 'headline',
      render: (_value, item) => (
        <Space orientation="vertical" size={2}>
          <span style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
            {item.headline}
          </span>
          {item.original_headline && (
            <Typography.Text type="secondary">
              Original: {item.original_headline}
            </Typography.Text>
          )}
          {inBlueprint.has(Number(item.id)) && (
            <Tag color="green">In this blueprint</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Tags',
      key: 'tags',
      width: '30%',
      render: (_value, item) => (
        <Space size={[4, 4]} wrap>
          {item.tags
            .filter(tag => {
              const category = categories[String(tag.tag_category)] || '';
              return category.startsWith('ai_') && category !== 'ai_curation';
            })
            .map(tag => (
              <Tag key={tag.id}>
                {CATEGORY_LABELS[categories[String(tag.tag_category)]] || 'Tag'}
                : {labels[String(tag.id)] || tag.text}
              </Tag>
            ))}
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title="Add AI Curated news"
      open={open}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={adding}>
          Cancel
        </Button>,
        <Button
          key="add"
          type="primary"
          loading={adding}
          disabled={!selected.length}
          onClick={() => void add()}
        >
          {selected.length
            ? `Add ${selected.length} ${selected.length === 1 ? 'story' : 'stories'}`
            : 'Add stories'}
        </Button>,
      ]}
    >
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Paragraph style={{ margin: 0 }}>
          AI Curated stories are clean copies of the original news: no dates,
          names or market moves. Stories are searched within this exercise’s
          dates; a story outside them, or on a day the exercise skips, is
          refused.
        </Typography.Paragraph>
        <form
          onSubmit={event => {
            event.preventDefault();
            void load();
          }}
        >
          <Space wrap align="end">
            <Input
              aria-label="Search AI Curated news"
              placeholder="Words in the headline"
              value={query}
              onChange={event => setQuery(event.target.value)}
              allowClear
              style={{ width: 260 }}
            />
            <Input
              aria-label="From"
              type="date"
              value={start}
              onChange={event => setStart(event.target.value)}
            />
            <Input
              aria-label="To"
              type="date"
              value={end}
              onChange={event => setEnd(event.target.value)}
            />
            <Button
              htmlType="submit"
              icon={<SearchOutlined />}
              loading={loading}
            >
              Search
            </Button>
          </Space>
        </form>
        <TagFilter
          value={tags}
          onChange={value => {
            setTags(value);
            void load(false, value);
          }}
        />
        {error && <Alert type="error" showIcon title={error} />}
        <Table
          rowKey={item => Number(item.id)}
          size="small"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          scroll={{ y: 420 }}
          rowSelection={{
            selectedRowKeys: selected,
            onChange: keys => setSelected(keys.map(Number)),
            getCheckboxProps: item => ({
              disabled: inBlueprint.has(Number(item.id)),
            }),
          }}
          locale={{
            emptyText: loading
              ? 'Searching…'
              : 'No AI Curated news matches these filters in these dates.',
          }}
        />
        {hasMore && (
          <Button onClick={() => void load(true)} loading={loading}>
            Load more
          </Button>
        )}
      </Space>
    </Modal>
  );
};

export default AddNewsModal;
